import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { residencesService } from '../../../services';
import { Button } from '../../../components';
import { ResidenceFormSkeleton } from '../../../skeletons';
import { AlertCircle, ArrowLeft, Upload, X } from 'lucide-react';

/**
 * Create/Edit Residence Page
 * Form to create or edit a residence (care home resident) for the care home manager
 */
const CreateResidence = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Check if we're in edit mode by looking at the URL pattern
  const isEditMode = !!id && (window.location.pathname.includes('/edit') || window.location.pathname.includes('/create'));
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    room_number: '',
    unit: '',
    emergency_contact: '',
    medical_notes: '',
    image_url: '',
  });
  const [previewImage, setPreviewImage] = useState('');
  const [imageInputMode, setImageInputMode] = useState('file'); // 'file' or 'url'
  const [imageUrl, setImageUrl] = useState('');
 
  useEffect(() => {
    if (isEditMode) {
      fetchResidence();
    }
  }, [id]);

  const fetchResidence = async () => {
    try {
      setLoading(true);
      const response = await residencesService.getResidenceById(id);
      if (response.success && response.data) {
        setFormData({
          name: response.data.name || '',
          age: response.data.age || '',
          room_number: response.data.room_number || '',
          unit: response.data.unit || '',
          emergency_contact: response.data.emergency_contact || '',
          medical_notes: response.data.medical_notes || '',
          image_url: response.data.image_url || '',
        });
        if (response.data.image_url) {
          setPreviewImage(response.data.image_url);
          setImageUrl(response.data.image_url);
        }
      } else {
        setError('Residence not found');
      }
    } catch (err) {
      console.error('Error fetching residence:', err);
      setError('Failed to load residence');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPreviewImage(base64);
        setFormData((prev) => ({
          ...prev,
          image_url: base64,
        }));
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setPreviewImage('');
    setImageUrl('');
    setFormData((prev) => ({
      ...prev,
      image_url: '',
    }));
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    if (url) {
      setPreviewImage(url);
      setFormData((prev) => ({
        ...prev,
        image_url: url,
      }));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Resident name is required');
      return;
    }

    if (!formData.room_number.trim()) {
      setError('Room number is required');
      return;
    }

    if (!formData.age || formData.age <= 0) {
      setError('Please enter a valid age');
      return;
    }

    try {
      setSaving(true);
      const response = isEditMode
        ? await residencesService.updateResidence(id, formData)
        : await residencesService.createResidence(formData);

      if (response.success) {
        navigate('/manager/residences');
      } else {
        setError(response.message || 'Failed to save residence');
      }
    } catch (err) {
      console.error('Error saving residence:', err);
      setError('Failed to save residence');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <ResidenceFormSkeleton />;
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/manager/residences')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-[#31373f] rounded-lg transition"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'Edit Residence' : 'Create New Residence'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode
              ? 'Update resident information'
              : 'Add a new care home resident to your organization'}
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white dark:bg-[#262b33] rounded-lg shadow p-6">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Resident Photo
            </label>
            
            {/* Image Input Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setImageInputMode('file')}
                className={`px-3 py-1 text-sm rounded transition ${
                  imageInputMode === 'file'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-[#31373f] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setImageInputMode('url')}
                className={`px-3 py-1 text-sm rounded transition ${
                  imageInputMode === 'url'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-[#31373f] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Paste URL
              </button>
            </div>

            {previewImage ? (
              <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-700">
                <img
                  src={previewImage}
                  alt="Resident"
                  className="w-full h-full object-cover"
                  onError={() => setError('Image URL is invalid or inaccessible')}
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded hover:bg-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : imageInputMode === 'file' ? (
              <label className="w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition">
                <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Click to upload or drag and drop</span>
                <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">PNG, JPG, GIF (max 5MB)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="space-y-2">
                <input
                  type="url"
                  value={imageUrl}
                  onChange={handleImageUrlChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">Enter a valid image URL</p>
              </div>
            )}
          </div>

          {/* Name Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Resident Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Jane Smith"
              className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Age Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Age *
            </label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              placeholder="75"
              min="1"
              max="150"
              className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Room and unit are separate fields — a resident in room 104 of
              the Bluebell unit needs both recorded, not one combined string. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Room Number *
              </label>
              <input
                type="text"
                name="room_number"
                value={formData.room_number}
                onChange={handleChange}
                placeholder="104"
                className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Unit
              </label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                placeholder="Bluebell"
                className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Emergency Contact Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Emergency Contact
            </label>
            <input
              type="tel"
              name="emergency_contact"
              value={formData.emergency_contact}
              onChange={handleChange}
              placeholder="John Smith +44 7700 900000"
              className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
            />
          </div>

          {/* Medical Notes Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Medical Notes
            </label>
            <textarea
              name="medical_notes"
              value={formData.medical_notes}
              onChange={handleChange}
              placeholder="Enter any relevant medical information, allergies, conditions, etc."
              rows="4"
              className="w-full px-4 py-2 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/manager/residences')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              className="flex-1"
            >
              {isEditMode ? 'Update Residence' : 'Create Residence'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateResidence;
